import { withNamespaces } from "react-i18next";
import DefaultAvatar from "src/assets/images/users/def.png";
import { Card, CardBody, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";
import DateUtils from "src/services/utils/DateUtils";
import { STANDARD_DATE_FORMAT } from "src/components/constants";
import { DEFAULT_TASK_PRIORITIES } from "./constants";

/**
 * Shared kanban task card.
 *
 * Customization props (modules can override presentation without forking):
 * - statuses / priorities: maps keyed by status/priority value
 * - getTaskLink: (data) => string | location object
 * - onTaskLinkClick: (data, event) => void
 * - renderTaskId: (data, t) => ReactNode
 * - renderSubtitle: (data, t) => ReactNode
 * - renderStatus / renderPriority: optional full overrides
 */
const TaskCardItem = ({
    t,
    data,
    statuses = {},
    priorities = DEFAULT_TASK_PRIORITIES,
    getTaskLink,
    onTaskLinkClick,
    renderTaskId,
    renderSubtitle,
    renderStatus,
    renderPriority,
}) => {
    const dateUtils = new DateUtils();
    const linkTo = typeof getTaskLink === "function" ? getTaskLink(data) : "#";

    const handleLinkClick = (event) => {
        if (typeof onTaskLinkClick === "function") {
            onTaskLinkClick(data, event);
        }
    };

    const defaultRenderStatus = () => {
        const statusKey = data?.status;
        const statusData =
            statuses?.[statusKey] ||
            statuses?.[String(statusKey || "").toUpperCase()] ||
            statuses?.[String(statusKey || "").toLowerCase()];

        if (!statusData && !statusKey) {
            return null;
        }

        if (statusData?.color) {
            return (
                <span
                    className="me-1 dt-task-status text-dark"
                    style={{
                        backgroundColor: `${statusData.color}`,
                    }}
                >
                    {t(statusData?.title || statusKey)}
                </span>
            );
        }

        const normalized = String(statusKey || "").toLowerCase();

        return (
            <span
                className={`me-1 badge badge-success ${
                    normalized === "new"
                        ? "bg-success"
                        : normalized === "in_progress"
                        ? "bg-primary"
                        : normalized === "closed"
                        ? "bg-danger"
                        : "bg-secondary"
                }`}
                style={{
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {t(statusData?.title || statusData?.label || statusKey)}
            </span>
        );
    };

    const defaultRenderPriority = () => {
        const priorityKey = data?.priority;
        const priorityData =
            priorities?.[priorityKey] ||
            priorities?.[String(priorityKey || "").toLowerCase()];

        if (!priorityKey) {
            return null;
        }

        if (priorityData?.color) {
            return (
                <span
                    className="me-1 dt-task-status text-capitalize text-dark"
                    style={{
                        backgroundColor: `${priorityData.color}`,
                    }}
                >
                    <span>{priorityData?.icon}</span>
                    {t(priorityData?.title || priorityKey)}
                </span>
            );
        }

        const normalized = String(priorityKey || "").toLowerCase();

        return (
            <span
                className={`me-1 badge badge-success text-capitalize ${
                    normalized === "low"
                        ? "bg-info"
                        : normalized === "medium"
                        ? "bg-success"
                        : normalized === "high"
                        ? "bg-danger"
                        : "bg-secondary"
                }`}
                style={{
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <span
                    style={{
                        marginRight: 4,
                        position: "relative",
                        top: -1,
                    }}
                >
                    {priorityData?.icon}
                </span>
                {t(priorityData?.title || priorityKey)}
            </span>
        );
    };

    return (
        <Card className="task-box">
            <div className="progress-container">
                <div className="progress-bar" />
            </div>
            <CardBody className="borad-width">
                <div className="float-end ms-2">
                    <span>
                        {`${t("Deadline")}: ${dateUtils.convertTimeStampToDate(
                            data?.endDate || data?.date,
                            STANDARD_DATE_FORMAT
                        )}`}
                    </span>
                </div>

                <div className="mb-3">
                    <Link to={linkTo || "#"} onClick={handleLinkClick}>
                        <span>
                            {typeof renderTaskId === "function"
                                ? renderTaskId(data, t)
                                : `#T${data?.secondaryId || ""}`}
                        </span>
                    </Link>
                </div>

                <div>
                    {data?.title ? (
                        <h5 className="mb-4">
                            <Link to={linkTo || "#"} onClick={handleLinkClick}>
                                <span className="text-dark">
                                    {data.isRecurring && data.recurrenceSecondaryId ? (
                                        <i className="ri-repeat-2-line text-info me-1 font-size-18 align-middle" />
                                    ) : null}
                                    {data.title}
                                    {data.isRecurring && data.recurrenceSecondaryId
                                        ? `-${data.recurrenceSecondaryId}`
                                        : ""}
                                </span>
                            </Link>
                        </h5>
                    ) : null}

                    {typeof renderSubtitle === "function" ? renderSubtitle(data, t) : null}
                </div>

                <div className="mb-1">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-inline-flex align-items-center mb-0">
                            <span className="me-2 d-lg-block d-none">{`${t("Team")} : `}</span>

                            {(data?.team || []).map((member, key) => (
                                <div className="team-member me-1 d-flex align-items-center" key={key}>
                                    <Link
                                        to="#"
                                        className="team-member d-inline-block"
                                        id={`owner-${member.id}`}
                                    >
                                        <div className="avatar-xs">
                                            <span
                                                className="avatar-title bg-soft-primary text-primary rounded-circle text-capitalize"
                                                style={{ overflow: "hidden" }}
                                            >
                                                <img
                                                    alt="member's avatar"
                                                    className="w-100 h-100 d-block"
                                                    src={member.avatar ? member.avatar : DefaultAvatar}
                                                    style={{
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            </span>
                                        </div>
                                    </Link>
                                    <UncontrolledTooltip target={`owner-${member.id}`} placement="top">
                                        <div className="text-capitalize text-left">
                                            {t(member.type)}:
                                            <br />
                                            {member.name}
                                        </div>
                                    </UncontrolledTooltip>
                                </div>
                            ))}
                        </div>

                        <div className="mb-0 text-right d-flex align-items-center justify-content-between">
                            {data?.status
                                ? typeof renderStatus === "function"
                                    ? renderStatus(data, t)
                                    : defaultRenderStatus()
                                : null}
                            {data?.priority
                                ? typeof renderPriority === "function"
                                    ? renderPriority(data, t)
                                    : defaultRenderPriority()
                                : null}
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(TaskCardItem);
